// Lib
import { RefObject, useEffect, useRef } from 'react';

// Utils
import { clearInboxReturnState, getInboxReturnState, saveInboxReturnState } from './inboxReturnStateService';
import findScrollableDomParent from '../../utils/dom/findScrollableDomParent';

type InboxReturnStateReturnType = {
    cleanupOnOrchestrationEnd: () => void;
    saveInboxReturnStateToLocalStorage: () => void;
};

/**
 * Restores the inbox return state when the component mounts and saves this state when the component unmounts.
 */
export default function useInboxReturnState(
    isInboxOpen: boolean,
    currentBoardId: string,
    inboxListContainerRef: RefObject<HTMLDivElement>,
): InboxReturnStateReturnType {
    const isBatchedRenderComplete = useRef(false);

    const applyReturnStateToList = () => {
        if (!isInboxOpen) return;

        const inboxReturnState = getInboxReturnState(currentBoardId);
        if (!inboxReturnState) return;

        // Delay the scroll restoration to the next frame to ensure that the inbox list container has been rendered
        requestAnimationFrame(() => {
            if (!inboxListContainerRef.current) return;

            if (!isBatchedRenderComplete.current) {
                inboxListContainerRef.current.style.height = `${inboxReturnState.height}px`;
            }

            const scrollableSectionParent = findScrollableDomParent(inboxListContainerRef.current);

            if (scrollableSectionParent) {
                scrollableSectionParent.scrollTop = inboxReturnState.scrollTop;
            }
        });
    };

    useEffect(() => {
        if (isInboxOpen) {
            applyReturnStateToList();
        } else {
            isBatchedRenderComplete.current = false;
        }
    }, [isInboxOpen]);

    useEffect(() => {
        const onBeforeUnload = () => {
            clearInboxReturnState();
        };

        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, []);

    const saveInboxReturnStateToLocalStorage = () => {
        if (!inboxListContainerRef?.current) return;

        const scrollableSectionParent = findScrollableDomParent(inboxListContainerRef.current);
        const scrollTop = scrollableSectionParent?.scrollTop || 0;
        const boundingClientRect = inboxListContainerRef.current.getBoundingClientRect();

        saveInboxReturnState(currentBoardId, { scrollTop, height: boundingClientRect.height });
    };

    /**
     * When the OrchestratedList finishes rendering all elements, we want to remove the height from the list
     * so that it will be rendered entirely based on the content, not the previously saved height of the list.
     */
    const cleanupOnOrchestrationEnd = () => {
        isBatchedRenderComplete.current = true;

        inboxListContainerRef?.current?.style.removeProperty('height');
    };

    return { cleanupOnOrchestrationEnd, saveInboxReturnStateToLocalStorage };
}
